import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { addToast } from '@/app/slices/toast';
import type { AppDispatch } from '@/app/store';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useRemoveSemenFromShipment } from '@/services/semen-service/hooks';
import type { ShipmentSemen } from '@/services/shipping/types';

export type UseRemoveSemenFromShipmentWorkflowOptions = {
  shipmentId?: string | null;
  dispatch: AppDispatch;
  onSuccess?: () => void;
  onError?: () => void;
};

export function useRemoveSemenFromShipmentWorkflow({
  shipmentId,
  dispatch,
  onSuccess,
  onError,
}: UseRemoveSemenFromShipmentWorkflowOptions) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const confirmWorkflows = useFeatureFlag('shipping_confirm_workflows');
  const { mutateAsync: removeSemenFromShipment } = useRemoveSemenFromShipment();

  const runWorkflow = useCallback(
    async (semen: ShipmentSemen[]) => {
      if (!shipmentId || semen.length === 0) return;

      try {
        const result = await removeSemenFromShipment({
          shipmentId,
          semenIds: semen.map((item) => item.id),
        });

        if (result && result.success === false) {
          throw new Error(
            result.message ?? t('shipping.semenWorkflows.errorDetail'),
          );
        }

        dispatch(
          addToast({
            severity: 'success',
            summary: t('shipping.semenWorkflows.successSummary'),
            detail: t('shipping.semenWorkflows.successDetail'),
            life: ToastLife.LONG,
          }),
        );
        if (onSuccess) onSuccess();
      } catch (error) {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('shipping.semenWorkflows.errorSummary'),
            detail: `${t('shipping.semenWorkflows.errorDetail')} ${
              error instanceof Error ? error.message : String(error)
            }`,
            life: ToastLife.LONG,
          }),
        );
        if (onError) onError();
      }
    },
    [dispatch, onError, onSuccess, removeSemenFromShipment, shipmentId, t],
  );

  const executeWorkflow = useCallback(
    (semen: ShipmentSemen[]) => {
      if (confirmWorkflows) {
        confirm({
          title: t('shipping.semenWorkflows.confirmTitle'),
          description: (
            <>
              {t('shipping.semenWorkflows.confirmDescription', {
                count: semen.length,
              })}
            </>
          ),
          confirmLabel: 'OK',
          cancelLabel: 'Cancel',
          onConfirm() {
            runWorkflow(semen);
          },
        });
        return;
      }

      return runWorkflow(semen);
    },
    [confirm, confirmWorkflows, runWorkflow, t],
  );

  return { executeWorkflow };
}
